import * as React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import { connect } from 'react-redux'
import { GET_INSTRUCTOR_AVAILABILITY } from '@queries/instructorAvailability'
import { GET_INSTRUCTOR_UNAVAILABILITY } from '@queries/instructorUnAvailability'

import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { StoreProps } from '@interfaces/StoreState'
import { UnAvailabilityAPIResponse } from '@interfaces/InstructorUnAvailability'
import { AvailabilityAPIResponse } from '@interfaces/InstructorAvailability'
import { UserProfileProps } from '@interfaces/UserProfile'
import PortalInstructorAvailabilities from '@classes/InstructorAvailabilities'
import PortalInstructorUnAvailabilities from '@classes/InstructorUnAvailabilities'
import PortalInstructor from '@classes/Instructor'
import { isEmpty, isEqual } from 'lodash'
import moment from 'moment'
import { DATE_FORMAT } from '@utils/constants'

export interface IQueryPageResults extends ISAvailability {
  refetch: Function
}

export interface ISAvailability {
  loading: boolean
  error: boolean
  Availabilities: PortalInstructorAvailabilities | null
  UnAvailabilities: PortalInstructorUnAvailabilities | null
}

export interface IPQueryPageData extends ISAvailability {
  profile?: UserProfileProps
  children: (results: IQueryPageResults) => {}
  refetch: Function
}

type Props = WithApolloClient<IPQueryPageData & WithUserTimeProps & InjectedIntlProps>

class QueryAvailabilityPage extends React.Component<Props, ISAvailability> {
  _isMounted = false
  availAbortCtrl = new AbortController()
  unAvailAbortCtrl = new AbortController()

  state = {
    loading: false,
    error: false,
    Availabilities: null,
    UnAvailabilities: null,
  }

  async componentDidMount() {
    const { profile } = this.props
    this._isMounted = true

    if (!isEmpty(profile)) {
      this.fetchSchedules()
    }
  }

  async componentDidUpdate(prevProps: IPQueryPageData) {
    const { profile } = this.props
    if (!isEqual(prevProps.profile, profile) && !isEmpty(profile)) {
      this.fetchSchedules()
    }
  }

  fetchSchedules = async (FromDate?: string, EndDate?: string) => {
    const { client, profile } = this.props
    const InstructorId = profile.InstructorProfile?.instructorInfo?.Id
    const Instructor = new PortalInstructor(profile.InstructorProfile?.instructorInfo)

    if (this._isMounted) {
      this.setState({
        loading: true,
      })
    }

    try {
      const date = moment(Instructor.CurrentDate)
      let dates = {
        FromDate: date.clone().startOf('week').format(DATE_FORMAT.BASIC),
        EndDate: date.clone().endOf('week').format(DATE_FORMAT.BASIC),
      }
      if (FromDate && EndDate && moment(FromDate).isValid() && moment(EndDate).isValid()) {
        dates = {
          FromDate,
          EndDate,
        }
      }

      console.log('Fetching schedules for dates (QueryPageData):', { FromDate, EndDate, dates })
      const availRes = await client.query<AvailabilityAPIResponse>({
        query: GET_INSTRUCTOR_AVAILABILITY,
        variables: {
          InstructorId,
          ...dates,
        },
        fetchPolicy: 'no-cache',
        context: {
          fetchOptions: {
            signal: this.availAbortCtrl.signal,
          },
        },
      })
      const unAvailRes = await client.query<UnAvailabilityAPIResponse>({
        query: GET_INSTRUCTOR_UNAVAILABILITY,
        variables: {
          InstructorId,
        },
        fetchPolicy: 'no-cache',
        context: {
          fetchOptions: {
            signal: this.unAvailAbortCtrl.signal,
          },
        },
      })

      if (this._isMounted) {
        const Availabilities = new PortalInstructorAvailabilities(
          availRes.data?.getInstructorAvailability?.ServiceResult,
          Instructor
        )
        const UnAvailabilities = new PortalInstructorUnAvailabilities(
          unAvailRes.data?.getInstructorUnAvailability?.ServiceResult,
          Instructor
        )

        this.setState({
          loading: false,
          error: false,
          Availabilities,
          UnAvailabilities,
        })
      }
    } catch (err) {
      if (this._isMounted) {
        this.setState({
          loading: false,
          error: true,
        })
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  render() {
    const { children } = this.props
    const { loading, error, Availabilities, UnAvailabilities } = this.state

    return children({
      loading,
      error,
      Availabilities,
      UnAvailabilities,
      refetch: this.fetchSchedules,
    })
  }
}

const mapStateToProps = ({ userProfile }: StoreProps) => ({
  profile: userProfile.info,
})

const withConnect = connect(mapStateToProps)

export default withConnect(injectIntl(withApollo(WithUserTimeHOC(QueryAvailabilityPage))))
